import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  CheckCircle2,
  AlertTriangle,
  ArrowLeft,
  Loader2,
  Calendar as CalIcon,
  User,
} from 'lucide-react';
import { toast } from 'sonner';
import { DailyReportForm } from '../../components/DailyReportForm';

/**
 * Public tokenized Daily-report form (from email link).
 *
 * Route: /daily-report/:token
 * Uses same Mongo doc as the internal CRM "My report today" page.
 */
export default function DailyReportPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

   
  useEffect(() => {
    let alive = true;
    setLoading(true);   
    api
      .get(`/public/daily-report/${token}`)
      .then((r) => {
        if (!alive) return;
        setTimeout(() => alive && setReport(r.data?.report || null), 0);
      })
      .catch((e) => {
        if (!alive) return;
        const s = e?.response?.status;
        setTimeout(() => alive && setError(
          s === 404
            ? 'Il link è scaduto o non valido. Compila il report direttamente nel CRM.'
            : 'Errore nel caricamento. Riprova fra qualche minuto.'
        ), 0);
      })
      .finally(() => alive && setTimeout(() => alive && setLoading(false), 0));
    return () => {
      alive = false;
    };
  }, [token]);
   

  const onSubmit = async (payload) => {
    setSubmitting(true);
    try {
      const r = await api.post(`/public/daily-report/${token}`, payload);
      setDone(r.data || { ok: true });
      toast.success('Report inviato. Grazie!');
    } catch (e) {
      const s = e?.response?.status;
      if (s === 404) toast.error('Link non valido o scaduto.');
      else toast.error("Errore durante l'invio. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <CenteredShell>
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Caricamento report…</span>
        </div>
      </CenteredShell>
    );
  }

  if (error) {
    return (
      <CenteredShell>
        <Card className="max-w-md w-full">
          <CardContent className="p-6 space-y-4 text-center">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
            <h1 className="font-serif text-xl text-[hsl(var(--primary))]">Link non disponibile</h1>
            <p className="text-sm text-muted-foreground">{error}</p>
            <p className="text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded-lg p-3">
              💡 Apri il CRM con le tue credenziali e vai su <b>&quot;Il mio report&quot;</b> per compilare il report di oggi.
              I dati che inserisci lì finiscono nello stesso posto del link via email.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Link
                to="/login"
                state={{ from: { pathname: `/daily-report/${token}` } }}
                className="inline-block"
              >
                <Button className="bg-[var(--gp-gold)] !text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)] font-semibold w-full sm:w-auto" data-testid="daily-report-go-crm">
                  Vai al CRM
                </Button>
              </Link>
              <Link to="/" className="inline-block">
                <Button variant="outline" data-testid="daily-report-back-home" className="w-full sm:w-auto">
                  <ArrowLeft className="w-4 h-4 mr-1.5" /> Torna alla home
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </CenteredShell>
    );
  }

  if (done) {
    return (
      <CenteredShell>
        <Card className="max-w-md w-full">
          <CardContent className="p-6 space-y-4 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
            <h1 className="font-serif text-2xl text-[hsl(var(--primary))]">
              {done.already_submitted ? 'Report già inviato' : 'Report inviato'}
            </h1>
            <p className="text-sm text-muted-foreground">
              Grazie. I tuoi numeri sono stati registrati nel CRM.
            </p>
            <Link to="/" className="inline-block">
              <Button variant="outline" data-testid="daily-report-done-home">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Torna alla home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </CenteredShell>
    );
  }

  const isReadOnly = report?.status === 'submitted';

  return (
    <div className="min-h-screen bg-[var(--surface-1)] py-10 px-4 md:px-6">
      <div className="max-w-3xl mx-auto" data-testid="daily-report-page">
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--gp-gold)] font-semibold">
            Green Paradise · CRM
          </div>
          <h1 className="font-serif text-2xl md:text-3xl text-[hsl(var(--primary))] mt-1">
            Daily report
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Il modulo è collegato al CRM: stesso record, stesso totale.
            Puoi compilare da qui o dall&apos;app interna.
          </p>
        </div>

        <Card className="border-border mb-4">
          <CardContent className="p-4 md:p-5">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium" data-testid="dr-agent-name">{report?.agent_name || '—'}</span>
                <span className="text-xs text-muted-foreground">
                  ({report?.agent_role === 'senior_associate'
                      ? 'Senior + Associate'
                      : report?.agent_role === 'senior'
                        ? 'Senior'
                        : 'Associate'})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CalIcon className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium" data-testid="dr-date">{report?.date}</span>
              </div>
              {isReadOnly && (
                <div className="ml-auto text-xs inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <CheckCircle2 className="w-3 h-3" />
                  Submitted
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <DailyReportForm
          report={report}
          onSubmit={onSubmit}
          submitting={submitting}
          readOnly={isReadOnly}
        />

        <p className="mt-6 text-[11px] text-muted-foreground/80 text-center">
          Green Paradise · HCF Group · dati confidenziali.
        </p>
      </div>
    </div>
  );
}

function CenteredShell({ children }) {
  return (
    <div className="min-h-screen grid place-items-center bg-[var(--surface-1)] p-6">
      {children}
    </div>
  );
}
